import { TuiDocument } from './Document';
import { Layer } from './Layer';
import { Cell, Rect, rectUnion } from './types';

export interface Command {
  readonly label: string;
  apply(doc: TuiDocument): void;
  revert(doc: TuiDocument): void;
  /** Region the command touched, for dirty-rect rendering. */
  dirtyRect?(): Rect | null;
  /** Allows consecutive same-kind commands to merge (e.g. typing). */
  mergeWith?(next: Command): Command | null;
}

interface CellEdit {
  index: number;
  before: Cell;
  after: Cell;
}

/**
 * Accumulates cell writes on one or more layers and turns them into a single
 * undoable command. A whole brush stroke is one transaction, which is what
 * keeps a 400-cell drag from consuming 400 undo slots.
 */
export class CellPatch {
  private edits = new Map<string, Map<number, CellEdit>>();
  private dirty: Rect | null = null;

  constructor(readonly label: string) {}

  get isEmpty(): boolean {
    for (const m of this.edits.values()) if (m.size > 0) return false;
    return true;
  }

  get editCount(): number {
    let n = 0;
    for (const m of this.edits.values()) n += m.size;
    return n;
  }

  /** Records a write to (x, y) on `layer` and performs it immediately. */
  write(layer: Layer, x: number, y: number, cell: Cell): boolean {
    if (!layer.grid.inBounds(x, y)) return false;
    const index = layer.grid.index(x, y);
    let layerEdits = this.edits.get(layer.id);
    if (!layerEdits) {
      layerEdits = new Map();
      this.edits.set(layer.id, layerEdits);
    }
    const existing = layerEdits.get(index);
    const before = existing ? existing.before : layer.grid.getAt(index);
    const changed = layer.grid.set(x, y, cell);
    if (!changed && !existing) return false;
    layerEdits.set(index, { index, before, after: layer.grid.getAt(index) });
    this.dirty = rectUnion(this.dirty, { x, y, width: 1, height: 1 });
    return changed;
  }

  /** Drops edits whose before/after are identical (no-op strokes). */
  prune(): void {
    for (const [layerId, m] of this.edits) {
      for (const [index, e] of m) {
        if (
          e.before.char === e.after.char &&
          e.before.fg === e.after.fg &&
          e.before.bg === e.after.bg &&
          e.before.attrs === e.after.attrs
        ) {
          m.delete(index);
        }
      }
      if (m.size === 0) this.edits.delete(layerId);
    }
  }

  toCommand(): Command | null {
    this.prune();
    if (this.isEmpty) return null;
    const snapshot = new Map<string, CellEdit[]>();
    for (const [layerId, m] of this.edits) snapshot.set(layerId, [...m.values()]);
    const dirty = this.dirty;
    const label = this.label;
    return {
      label,
      apply(doc) {
        for (const [layerId, edits] of snapshot) {
          const layer = doc.layerById(layerId);
          if (!layer) continue;
          for (const e of edits) layer.grid.setAt(e.index, e.after);
        }
        doc.touch();
      },
      revert(doc) {
        for (const [layerId, edits] of snapshot) {
          const layer = doc.layerById(layerId);
          if (!layer) continue;
          for (const e of edits) layer.grid.setAt(e.index, e.before);
        }
        doc.touch();
      },
      dirtyRect: () => dirty,
    };
  }
}

/** Escape hatch for structural edits (layer add/remove/reorder, resize). */
export function makeCommand(
  label: string,
  apply: (doc: TuiDocument) => void,
  revert: (doc: TuiDocument) => void,
): Command {
  return {
    label,
    apply(doc) {
      apply(doc);
      doc.touch();
    },
    revert(doc) {
      revert(doc);
      doc.touch();
    },
  };
}

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  depth: number;
}

export class History {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private listeners = new Set<(state: HistoryState) => void>();

  constructor(private readonly limit = 200) {}

  /** Runs a command and records it. */
  execute(doc: TuiDocument, command: Command): void {
    command.apply(doc);
    this.push(command);
  }

  /** Records a command whose effect has already been applied. */
  push(command: Command): void {
    this.undoStack.push(command);
    if (this.undoStack.length > this.limit) this.undoStack.shift();
    this.redoStack.length = 0;
    this.emit();
  }

  undo(doc: TuiDocument): Command | null {
    const cmd = this.undoStack.pop();
    if (!cmd) return null;
    cmd.revert(doc);
    this.redoStack.push(cmd);
    this.emit();
    return cmd;
  }

  redo(doc: TuiDocument): Command | null {
    const cmd = this.redoStack.pop();
    if (!cmd) return null;
    cmd.apply(doc);
    this.undoStack.push(cmd);
    this.emit();
    return cmd;
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.emit();
  }

  get state(): HistoryState {
    return {
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoLabel: this.undoStack.at(-1)?.label ?? null,
      redoLabel: this.redoStack.at(-1)?.label ?? null,
      depth: this.undoStack.length,
    };
  }

  subscribe(fn: (state: HistoryState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const s = this.state;
    this.listeners.forEach((fn) => fn(s));
  }
}
